/**
 * 路由配置
 * 功能用途：定义应用的所有路由规则，包括路由守卫和权限控制
 */

import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'

// 路由配置
const routes = [
  // 登录页
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { public: true, title: '登录' }
  },
  
  // 主布局
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/components/layout/index.vue'),
    redirect: '/dashboard',
    children: [
      // 仪表盘
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '仪表盘', icon: 'Odometer' }
      },
      // 监控目标
      {
        path: '/targets',
        name: 'Targets',
        component: () => import('@/views/targets/index.vue'),
        meta: { title: '监控目标', icon: 'Aim' }
      },
      // 告警规则
      {
        path: '/alert-rules',
        name: 'AlertRules',
        component: () => import('@/views/alert-rules/index.vue'),
        meta: { title: '告警规则', icon: 'Bell' }
      },
      // 告警历史
      {
        path: '/alert-history',
        name: 'AlertHistory',
        component: () => import('@/views/alert-history/index.vue'),
        meta: { title: '告警历史', icon: 'Warning' }
      },
      // 通知中心
      {
        path: '/notifications',
        name: 'Notifications',
        component: () => import('@/views/notifications/index.vue'),
        meta: { title: '通知中心', icon: 'Message' }
      },
      // 站点设置
      {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/settings/index.vue'),
        meta: { title: '站点设置', icon: 'Setting' }
      },
      // 安全设置
      {
        path: '/settings/security',
        name: 'SecuritySettings',
        component: () => import('@/views/settings/security.vue'),
        meta: { title: '安全设置', icon: 'Lock' }
      },
      // 个人设置
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/settings/profile.vue'),
        meta: { title: '个人设置', icon: 'User' }
      },
      // 账号安全
      {
        path: '/settings/account',
        name: 'AccountSecurity',
        component: () => import('@/views/settings/profile.vue'),
        meta: { title: '账号安全', icon: 'Lock' }
      },
      // 分类标签
      {
        path: '/tags',
        name: 'Tags',
        component: () => import('@/views/tags/index.vue'),
        meta: { title: '分类标签', icon: 'CollectionTag' }
      },
      // 监控模板
      {
        path: '/templates',
        name: 'Templates',
        component: () => import('@/views/templates/index.vue'),
        meta: { title: '监控模板', icon: 'DocumentCopy' }
      },
      // 规则测试
      {
        path: '/rule-test',
        name: 'RuleTest',
        component: () => import('@/views/rule-test/index.vue'),
        meta: { title: '规则测试', icon: 'Test' }
      },
      // 调度器
      {
        path: '/scheduler',
        name: 'Scheduler',
        component: () => import('@/views/scheduler/index.vue'),
        meta: { title: '调度器', icon: 'Timer' }
      },
      // 调度器配置
      {
        path: '/scheduler-config',
        name: 'SchedulerConfig',
        component: () => import('@/views/scheduler-config/index.vue'),
        meta: { title: '调度器配置', icon: 'Setting' }
      },
      // 系统日志
      {
        path: '/logs',
        name: 'Logs',
        component: () => import('@/views/operation-logs/index.vue'),
        meta: { title: '系统日志', icon: 'Document' }
      },
      // 系统事件
      {
        path: '/system-events',
        name: 'SystemEvents',
        component: () => import('@/views/system-events/index.vue'),
        meta: { title: '系统事件', icon: 'Warning' }
      },
      // 系统清理
      {
        path: '/cleanup',
        name: 'Cleanup',
        component: () => import('@/views/cleanup/index.vue'),
        meta: { title: '系统清理', icon: 'Delete' }
      },
      // 系统健康
      {
        path: '/health',
        name: 'Health',
        component: () => import('@/views/health/index.vue'),
        meta: { title: '系统健康', icon: 'CircleCheck' }
      },
      // 状态趋势图
      {
        path: '/status-trend',
        name: 'StatusTrend',
        component: () => import('@/views/status-trend/index.vue'),
        meta: { title: '状态趋势图', icon: 'TrendCharts' }
      },
      // 联系人
      {
        path: '/recipients',
        name: 'Recipients',
        component: () => import('@/views/recipients/index.vue'),
        meta: { title: '联系人', icon: 'User' }
      },
      // 通知渠道
      {
        path: '/notification-channels',
        name: 'NotificationChannels',
        component: () => import('@/views/notification-channels/index.vue'),
        meta: { title: '通知渠道', icon: 'Message' }
      },
      // 系统组件
      {
        path: '/components',
        name: 'Components',
        component: () => import('@/views/components/index.vue'),
        meta: { title: '系统组件', icon: 'Grid' }
      },
      // 操作日志
      {
        path: '/operation-logs',
        name: 'OperationLogs',
        component: () => import('@/views/operation-logs/index.vue'),
        meta: { title: '操作日志', icon: 'Document' }
      },
      // 任务管理
      {
        path: '/tasks',
        name: 'Tasks',
        component: () => import('@/views/tasks/index.vue'),
        meta: { title: '任务管理', icon: 'List' }
      },
      // 帮助中心
      {
        path: '/help',
        name: 'Help',
        component: () => import('@/views/help/index.vue'),
        meta: { title: '帮助中心', icon: 'QuestionFilled' }
      },
      // 巡检日志
      {
        path: '/inspection-logs',
        name: 'InspectionLogs',
        component: () => import('@/views/inspection-logs/index.vue'),
        meta: { title: '巡检日志', icon: 'Search' }
      },
      // 告警升级策略
      {
        path: '/alert-escalation',
        name: 'AlertEscalation',
        component: () => import('@/views/alert-escalation/index.vue'),
        meta: { title: '告警升级策略', icon: 'Top' }
      },
      // 告警屏蔽
      {
        path: '/alert-mutes',
        name: 'AlertMutes',
        component: () => import('@/views/alert-mutes/index.vue'),
        meta: { title: '告警屏蔽', icon: 'Mute' }
      },
      // 规则管理（重定向到告警规则）
      {
        path: '/rules',
        redirect: '/alert-rules'
      }
    ]
  },
  
  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '页面未找到' }
  }
]

// 创建路由实例
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// 路由守卫
router.beforeEach((to, from, next) => {
  // 设置页面标题
  document.title = to.meta.title ? `${to.meta.title} - 风雷宜` : '风雷宜'
  
  const userStore = useUserStore()
  
  // 公开页面直接放行
  if (to.meta.public) {
    next()
    return
  }
  
  // 检查是否已登录
  if (!userStore.token) {
    next('/login')
    return
  }
  
  next()
})

export default router
